import { useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, Chip, CircularProgress,
  Divider, IconButton, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { api } from '../../services/api';
import type { EmballageFacility, EmballageReportData, EmballageSavedReport } from '../../types';

const MAX_DAGE = 92;

const fmtKg = (kg: number) => new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(Math.round(kg));
const fmtTon = (kg: number) => new Intl.NumberFormat('da-DK', { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(kg / 1000);
const fmtDate = (iso: string) => (iso ? iso.split('T')[0].split('-').reverse().join('-') : '');

function defaultPeriod(): { from: string; to: string } {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  const from = new Date(Date.UTC(now.getFullYear(), q * 3, 1));
  const to = new Date(Date.UTC(now.getFullYear(), q * 3 + 3, 0));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

interface Props {
  onNotify: (msg: string, error?: boolean) => void;
}

export default function EmballageReportPanel({ onNotify }: Props) {
  const initial = useMemo(defaultPeriod, []);
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [facilities, setFacilities] = useState<EmballageFacility[]>([]);
  const [facilityId, setFacilityId] = useState('');
  const [report, setReport] = useState<EmballageReportData | null>(null);
  const [running, setRunning] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [statusText, setStatusText] = useState('');
  const [doneReport, setDoneReport] = useState<EmballageSavedReport | null>(null);
  const [saved, setSaved] = useState<EmballageSavedReport[]>([]);

  const loadSaved = async () => {
    try { setSaved(await api.getEmballageReports()); } catch { /* listen er ikke kritisk */ }
  };

  useEffect(() => {
    void (async () => {
      try {
        const f = (await api.getEmballageFacilities()).filter(x => x.active);
        setFacilities(f);
        setFacilityId(f.find(x => x.isDefault)?.id ?? f[0]?.id ?? '');
      } catch { /* vises som tom liste */ }
      await loadSaved();
    })();
  }, []);

  const dage = (Date.parse(to) - Date.parse(from)) / 86400000 + 1;
  const forLangPeriode = Number.isFinite(dage) && dage > MAX_DAGE;

  // Rapporten bygges som et job på serveren, fordi den kan tage minutter.
  // Vi følger status frem for at holde en request åben, som Heroku alligevel
  // afbryder efter 30 sekunder.
  const POLL_MS = 3000;

  const run = async () => {
    if (!facilityId) {
      onNotify('Vælg et modtageanlæg', true);
      return;
    }
    setRunning(true);
    setReport(null);
    setStatusText('Starter');
    try {
      const job = await api.startEmballageReport(from, to, facilityId);
      setJobId(job.id);
      await loadSaved();
    } catch (e) {
      setRunning(false);
      setStatusText('');
      onNotify(e instanceof Error ? e.message : 'Rapporten kunne ikke startes', true);
    }
  };

  // Spørger til jobbet indtil det er færdigt. Ryddes op hvis komponenten
  // forlades, så en gammel timer ikke skriver i en afmonteret visning.
  useEffect(() => {
    if (!jobId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      try {
        const r = await api.getEmballageReport(jobId);
        if (stopped) return;
        if (r.status === 'PENDING') {
          setStatusText('Henter data fra DORA');
          timer = setTimeout(tick, POLL_MS);
          return;
        }
        setRunning(false);
        setStatusText('');
        setJobId(null);
        await loadSaved();
        if (r.status === 'FAILED') {
          onNotify(r.error || 'Rapporten fejlede', true);
          return;
        }
        setReport(r.data ?? null);
        setDoneReport(r);
      } catch {
        if (stopped) return;
        // Et enkelt mislykket opslag betyder ikke at jobbet er dødt.
        timer = setTimeout(tick, POLL_MS);
      }
    };

    timer = setTimeout(tick, POLL_MS);
    return () => { stopped = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const downloadDone = async () => {
    if (!doneReport) return;
    try {
      await api.downloadEmballageReportPdf(doneReport.id, doneReport.filename);
    } catch {
      onNotify('PDF\'en kunne ikke hentes', true);
    }
  };

  const removeSaved = async (r: EmballageSavedReport) => {
    if (!window.confirm(`Slet rapporten til ${r.facilityName}?`)) return;
    try {
      await api.deleteEmballageReport(r.id);
      await loadSaved();
    } catch {
      onNotify('Rapporten blev ikke slettet', true);
    }
  };

  return (
    <Box>
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="Fra" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={from} onChange={e => setFrom(e.target.value)}
          />
          <TextField
            label="Til" type="date" size="small" InputLabelProps={{ shrink: true }}
            value={to} onChange={e => setTo(e.target.value)}
            helperText={forLangPeriode ? 'En anmodning må højst dække 3 måneder' : ' '}
            error={forLangPeriode}
          />
          <TextField
            select label="Modtageanlæg" size="small" sx={{ minWidth: 240 }}
            value={facilityId} onChange={e => setFacilityId(e.target.value)}
          >
            {facilities.length === 0 && <MenuItem value="">Intet oprettet</MenuItem>}
            {facilities.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={run} disabled={running || !facilityId}>
            {running ? 'Arbejder' : 'Lav rapport'}
          </Button>
          <Button
            variant="outlined" startIcon={<DownloadIcon />} onClick={downloadDone}
            disabled={!doneReport}
          >
            Hent PDF
          </Button>
        </Stack>
      </Card>

      {running && (
        <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">{statusText}</Typography>
        </Stack>
      )}

      {report && (
        <Box sx={{ mb: 4 }}>
          {report.lineCount === 0 ? (
            <Card variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Ingen indsamlinger i perioden.</Typography>
            </Card>
          ) : (
            <>
              <Stack direction="row" spacing={3} sx={{ mb: 2, flexWrap: 'wrap' }}>
                <Stat label="Virksomheder" value={String(report.customers.length)} />
                <Stat label="Indsamlinger" value={String(report.lineCount)} />
                <Stat label="Ton i alt" value={fmtTon(report.totalKg)} />
              </Stack>

              {report.warnings.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Stack spacing={0.5}>
                    {report.warnings.slice(0, 8).map((w, i) => <span key={i}>{w.message}</span>)}
                    {report.warnings.length > 8 && <span>{report.warnings.length - 8} flere</span>}
                  </Stack>
                </Alert>
              )}

              <Card variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fraktion</TableCell>
                      <TableCell align="right">Indsamlinger</TableCell>
                      <TableCell align="right">Ton</TableCell>
                      <TableCell align="right">Kg</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.totals.map(t => (
                      <TableRow key={t.fraction}>
                        <TableCell>{t.label}</TableCell>
                        <TableCell align="right">{t.lineCount}</TableCell>
                        <TableCell align="right">{fmtTon(t.kg)}</TableCell>
                        <TableCell align="right">{fmtKg(t.kg)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>I alt</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{report.lineCount}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtTon(report.totalKg)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtKg(report.totalKg)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Card>

              {report.customers.map(c => (
                <Accordion key={c.customerNumber} disableGutters variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                      <Typography sx={{ fontWeight: 600, flex: 1 }}>{c.customerName}</Typography>
                      {!c.cvr && <Chip size="small" color="warning" variant="outlined" label="Mangler CVR" />}
                      <Typography variant="body2" color="text.secondary">{c.lines.length} indsamlinger</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 90, textAlign: 'right' }}>
                        {fmtTon(c.totalKg)} ton
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
                      {c.perFraction.map(f => (
                        <Chip key={f.fraction} size="small" variant="outlined" label={`${f.label} ${fmtKg(f.kg)} kg`} />
                      ))}
                    </Stack>
                    <Divider sx={{ mb: 1 }} />
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Dato</TableCell>
                          <TableCell>Opgave</TableCell>
                          <TableCell>Fraktion</TableCell>
                          <TableCell align="right">Ton</TableCell>
                          <TableCell align="right">Kg</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {c.lines.map((l, i) => (
                          <TableRow key={`${l.cargoId}-${l.productNumber}-${i}`}>
                            <TableCell>{fmtDate(l.date)}</TableCell>
                            <TableCell>{l.cargoRef}</TableCell>
                            <TableCell>{l.eKode ? `${l.fractionLabel} (${l.eKode})` : l.fractionLabel}</TableCell>
                            <TableCell align="right">{fmtTon(l.kg)}</TableCell>
                            <TableCell align="right">{fmtKg(l.kg)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </>
          )}
        </Box>
      )}

      {saved.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Tidligere rapporter</Typography>
          <Card variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Modtageanlæg</TableCell>
                  <TableCell>Periode</TableCell>
                  <TableCell align="right">Virksomheder</TableCell>
                  <TableCell align="right">Ton</TableCell>
                  <TableCell>Lavet</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {saved.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.facilityName}</TableCell>
                    <TableCell>{fmtDate(r.periodFrom)} til {fmtDate(r.periodTo)}</TableCell>
                    <TableCell align="right">{r.customerCount}</TableCell>
                    <TableCell align="right">{fmtTon(r.totalKg)}</TableCell>
                    <TableCell>{new Date(r.generatedAt).toLocaleDateString('da-DK')}</TableCell>
                    <TableCell>
                      {r.status === 'DONE' ? '' : r.status === 'PENDING'
                        ? <Chip size="small" variant="outlined" label="Arbejder" />
                        : <Chip size="small" color="error" variant="outlined" label={r.error || 'Fejlede'} />}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" disabled={r.status !== 'DONE'} onClick={() => api.downloadEmballageReportPdf(r.id, r.filename)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => removeSaved(r)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Box>
      )}
    </Box>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{value}</Typography>
    </Box>
  );
}
